import { useEffect, useState, useCallback } from 'react'
import Api from 'services/Api'

type Params = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  url: string
  autoExecute?: boolean
  initialLoad?: boolean
}

export type ApiResponse<T> = T | null

type ReturnType<T, Tmeta> = {
  handleApi: <T, Tmeta = void>(payload?: Record<string, unknown>) => Promise<Pick<ReturnType<T, Tmeta>, 'isError' | 'metaData' | 'response'>>
  isError: boolean
  isLoading: boolean
  metaData: ApiResponse<Tmeta>
  response: ApiResponse<T>
}

const useApi = <T, Tmeta = void>({ method = 'GET', url, autoExecute = true, initialLoad = true }: Params): ReturnType<T, Tmeta> => {
  const [response, setResponse] = useState(null)
  const [metaData, setMetaData] = useState(null)
  const [isLoading, setIsLoading] = useState(initialLoad)
  const [isError, setIsError] = useState(false)

  const handleApi = useCallback<ReturnType<T, Tmeta>['handleApi']>(
    async payload => {
      setIsLoading(true)

      const { params, ...data } = payload || {}

      const { success, result, ...rest } = await Api({
        method,
        url,
        data,
        params: typeof params === 'object' ? { ...params, useNewReturn: true } : { useNewReturn: true }
      })

      if (success) {
        setResponse(result)
        setMetaData(rest)
      } else {
        setIsError(true)
      }

      setIsLoading(false)

      return { isError: !success, response: result, metaData: rest }
    },
    [method, url]
  )

  useEffect(() => {
    let cancelled = false // stale cancellation

    ;(async () => {
      if (cancelled || method !== 'GET' || !autoExecute) return

      await handleApi()
    })()

    return () => {
      cancelled = true
    }
  }, [handleApi, method, autoExecute])

  return {
    handleApi,
    isError,
    isLoading,
    metaData,
    response
  }
}

export default useApi
